import { Listbox, Transition } from "@headlessui/react";
import { CaretDown, CaretUp } from "@phosphor-icons/react";
import clsx from "clsx";
import { Fragment, useState } from "react";
import { FieldError } from "react-hook-form";
import FormInputMessage from "./FormInputError";
import FormInputLabel from "./FormInputLabel";
import InfoCard from "./InfoCard";
import { FormDropdownOption } from "./FormDropdown";
import FormCheckbox from "./FormCheckbox";

type DropdownPosition = "top" | "bottom";

export type FormMultiSelectDropdownProps<
    TOption extends FormDropdownOption<string | number>
> = {
    options: TOption[];
    label?: string;
    text: string;
    values: TOption[];
    onSelect: (option: TOption[]) => void;
    error?: FieldError;
    required?: boolean;
    disabled?: boolean;
    className?: string;
    infoText?: string;
    isLoadingOptions?: boolean;
    position?: DropdownPosition;
    hideLabel?: boolean;
    inputClassName?: string;
};

const FormMultiSelectDropdown = <
    TOption extends FormDropdownOption<string | number>
>({
    label,
    text,
    options,
    values,
    onSelect,
    error,
    required,
    className,
    infoText,
    disabled,
    isLoadingOptions,
    position = "bottom",
    hideLabel = false,
    inputClassName,
}: FormMultiSelectDropdownProps<TOption>) => {
    const [moreInfoIsExpanded, setMoreInfoIsExpanded] = useState(false);
    const moreInfoLabelId = `${label}-more-info`;
    const moreInfoPanelId = `${label}-more-info-panel`;
    const _disabled = disabled || isLoadingOptions;

    const handleOptionClick = (
        event: React.MouseEvent<HTMLElement, MouseEvent>,
        option: TOption
    ) => {
        event.preventDefault();

        if (values.includes(option)) {
            onSelect(values.filter((v) => v !== option));
        } else {
            onSelect([...values, option]);
        }
    };

    return (
        <div className={className}>
            {label && (
                <div className="flex justify-between">
                    <FormInputLabel
                        className={clsx(
                            "whitespace-nowrap",
                            hideLabel && "sr-only"
                        )}
                        required={required}
                    >
                        {label}
                    </FormInputLabel>
                    {infoText && (
                        <button
                            className="text-sm underline"
                            id={moreInfoLabelId}
                            aria-expanded={moreInfoIsExpanded}
                            aria-controls={moreInfoPanelId}
                            onClick={() =>
                                setMoreInfoIsExpanded((prev) => !prev)
                            }
                            aria-label={
                                moreInfoIsExpanded ? "hide info" : undefined
                            }
                            type="button"
                        >
                            <span className="sr-only">{label} </span>info
                        </button>
                    )}
                </div>
            )}
            <Listbox
                value={values}
                onChange={onSelect}
                disabled={_disabled}
                multiple
            >
                {({ open }) => (
                    <div className="relative">
                        <Listbox.Button
                            className={clsx(
                                "border relative text-left cursor-default border-surface-dark-5 w-full h-[3.25rem] px-4 rounded transition-colors pr-10",
                                "focus:bg-surface-light focus:outline-none focus:ring-2 focus:ring-soft-green-80",
                                "hover:bg-surface-light-1",
                                open && "z-50 ring-2 ring-soft-green-80",
                                !error && "bg-surface-light",
                                error &&
                                    "bg-red-95 outline-red-40 border-red-40 focus:ring-red-40 ring-red-40 focus:outline outline outline-2 pr-10",
                                open && error && "ring-0",
                                inputClassName,
                                _disabled && "border-disabled"
                            )}
                        >
                            <span
                                className={clsx(
                                    "block truncate",
                                    _disabled && "text-disabled-text"
                                )}
                            >
                                {isLoadingOptions
                                    ? "Loading..."
                                    : values.length > 0
                                    ? values.map((_) => _.label).join(", ")
                                    : text}
                            </span>
                            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4">
                                {open ? (
                                    <CaretUp
                                        className={clsx(
                                            "h-5 w-5 text-inherit",
                                            _disabled && "text-disabled-text"
                                        )}
                                        aria-hidden
                                    />
                                ) : (
                                    <CaretDown
                                        className={clsx(
                                            "h-5 w-5 text-inherit",
                                            _disabled && "text-disabled-text"
                                        )}
                                        aria-hidden
                                    />
                                )}
                            </span>
                        </Listbox.Button>
                        <Transition
                            as={Fragment}
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                        >
                            <Listbox.Options
                                className={clsx(
                                    "drop-shadow-lg rounded-xl absolute max-h-60 w-full overflow-auto",
                                    "scrollbar-thin scrollbar-thumb-scrollbar bg-white text-base",
                                    "focus:outline-none sm:text-sm z-50",
                                    position === "top" && "bottom-full mb-1",
                                    position === "bottom" && "mt-1",
                                    open && position === "bottom" && "border-t",
                                    open && position === "top" && "border-t"
                                )}
                            >
                                {options.map((option) => (
                                    <Listbox.Option
                                        key={option.value}
                                        className={({ active }) =>
                                            clsx(
                                                "relative cursor-default select-none py-3 px-4 transition-colors hover:bg-soft-green-80",
                                                active && "bg-soft-green-80"
                                            )
                                        }
                                        value={option}
                                        onClick={(event) => {
                                            handleOptionClick(event, option);
                                        }}
                                    >
                                        <FormCheckbox
                                            id={`checkbox-${option.value}`}
                                            checked={values.includes(option)}
                                            label={option.label}
                                        />
                                    </Listbox.Option>
                                ))}
                            </Listbox.Options>
                        </Transition>
                    </div>
                )}
            </Listbox>
            <FormInputMessage error={error} />
            {infoText && (
                <InfoCard
                    className="p-3 mt-4"
                    id={moreInfoPanelId}
                    aria-labelledby={moreInfoLabelId}
                    hidden={!moreInfoIsExpanded}
                >
                    {label && <h3 className="font-bold mb-1">{label}</h3>}
                    {infoText}
                </InfoCard>
            )}
        </div>
    );
};

export default FormMultiSelectDropdown;
